import Axios from "axios";

// Cache key prefix for localStorage
const CACHE_KEY_PREFIX = "coin_icon_cache_";
// Cache duration: 30 days (icons don't change frequently)
const CACHE_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Get coin icon from cache or fetch from CoinGecko API
 * @param {string} assetName - Asset symbol (e.g., "BTC", "ETH")
 * @param {Object} coinIdMap - Optional mapping of asset symbols to CoinGecko IDs
 * @returns {Promise<string|null>} Icon URL or null if not found
 */
export const getCoinIcon = async (assetName, coinIdMap = {}) => {
    if (!assetName) {
        return null;
    }

    const cacheKey = `${CACHE_KEY_PREFIX}${assetName.toUpperCase()}`;

    // Check cache first
    try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
            const { iconUrl, timestamp } = JSON.parse(cachedData);
            const now = Date.now();

            // Check if cache is still valid
            if (iconUrl && now - timestamp < CACHE_DURATION_MS) {
                return iconUrl;
            }

            // Cache expired, remove it
            localStorage.removeItem(cacheKey);
        }
    } catch (error) {
        // If cache read fails, continue to fetch
        console.warn("Failed to read coin icon cache:", error);
    }

    // Cache miss or expired - fetch from API
    try {
        // Map common Binance asset symbols to CoinGecko coin IDs
        const defaultCoinIdMap = {
            USDT: "tether",
            BNB: "binancecoin",
            BTC: "bitcoin",
            ETH: "ethereum",
            SOL: "solana",
            XRP: "ripple",
            ADA: "cardano",
            DOT: "polkadot",
            DOGE: "dogecoin",
            MATIC: "matic-network",
            AVAX: "avalanche-2",
            LINK: "chainlink",
            UNI: "uniswap",
            ATOM: "cosmos",
            LTC: "litecoin",
            BCH: "bitcoin-cash",
            XLM: "stellar",
            ALGO: "algorand",
            VET: "vechain",
            FIL: "filecoin",
            TRX: "tron",
            EOS: "eos",
            AAVE: "aave",
            MKR: "maker",
            COMP: "compound-governance-token",
            YFI: "yearn-finance",
            SNX: "havven",
            SUSHI: "sushi",
            CRV: "curve-dao-token",
            PEPE: "pepe",
        };

        // Merge provided map with default map
        const mergedCoinIdMap = { ...defaultCoinIdMap, ...coinIdMap };

        // Try mapped coin ID first, then fallback to lowercase asset name
        const coinId =
            mergedCoinIdMap[assetName.toUpperCase()] || assetName.toLowerCase();

        const api = `https://api.coingecko.com/api/v3/coins/${coinId}`;
        const response = await Axios.get(api, {
            params: {
                localization: false,
                tickers: false,
                market_data: false,
                community_data: false,
                developer_data: false,
                sparkline: false,
            },
        });

        if (response.data && response.data.image) {
            const iconUrl =
                response.data.image.small || response.data.image.large;

            // Cache the result
            try {
                const cacheData = {
                    iconUrl,
                    timestamp: Date.now(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (cacheError) {
                // If cache write fails, still return the icon
                console.warn("Failed to cache coin icon:", cacheError);
            }

            return iconUrl;
        }

        // No icon found - cache null result to avoid repeated failed requests
        try {
            const cacheData = {
                iconUrl: null,
                timestamp: Date.now(),
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (cacheError) {
            // Ignore cache write errors for null results
        }

        return null;
    } catch (error) {
        // If API call fails (e.g., 429 rate limit), check if we have a stale cache
        try {
            const cachedData = localStorage.getItem(cacheKey);
            if (cachedData) {
                const { iconUrl } = JSON.parse(cachedData);
                // Return stale cache if available (better than no icon)
                if (iconUrl) {
                    return iconUrl;
                }
            }
        } catch (cacheError) {
            // Ignore cache read errors
        }

        // If coin not found or API error, return null
        return null;
    }
};

/**
 * Clear coin icon cache for a specific asset
 * @param {string} assetName - Asset symbol to clear cache for
 */
export const clearCoinIconCache = (assetName) => {
    if (!assetName) return;
    const cacheKey = `${CACHE_KEY_PREFIX}${assetName.toUpperCase()}`;
    try {
        localStorage.removeItem(cacheKey);
    } catch (error) {
        console.warn("Failed to clear coin icon cache:", error);
    }
};

/**
 * Clear all coin icon caches
 */
export const clearAllCoinIconCaches = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
            if (key.startsWith(CACHE_KEY_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn("Failed to clear all coin icon caches:", error);
    }
};

/**
 * Get cache statistics (for debugging)
 * @returns {Object} Cache stats
 */
export const getCoinIconCacheStats = () => {
    try {
        const keys = Object.keys(localStorage);
        const cacheKeys = keys.filter((key) =>
            key.startsWith(CACHE_KEY_PREFIX)
        );
        const now = Date.now();
        let validCount = 0;
        let expiredCount = 0;

        cacheKeys.forEach((key) => {
            try {
                const cachedData = localStorage.getItem(key);
                if (cachedData) {
                    const { timestamp } = JSON.parse(cachedData);
                    if (now - timestamp < CACHE_DURATION_MS) {
                        validCount += 1;
                    } else {
                        expiredCount += 1;
                    }
                }
            } catch (error) {
                // Ignore parse errors
            }
        });

        return {
            total: cacheKeys.length,
            valid: validCount,
            expired: expiredCount,
        };
    } catch (error) {
        return { total: 0, valid: 0, expired: 0 };
    }
};
